import { createTheme, ThemeProvider } from '@mui/material'
import {
  DataGrid,
  enUS,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import type * as React from 'react'
import { useContext, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import type { DeviceInfo } from '../interfaces/DeviceIf'
import { LanguageContext } from '../util/LanguageContext'
import { ModalDeviceEditDialog } from './ModalDeviceEditDialog'
import { ModalDeviceSalesCompanyEditDialog } from './ModalDeviceSalesCompanyEditDialog'

type DeviceListProps = {
  items: DeviceInfo[]
  handleSelectionModelChange: (selectionModel: GridRowSelectionModel) => void
  onEdit: (id: string, newText: string) => void
  onSalesCompanyEdit: (id: string, newSalesCompany: string) => void
  gridSelection: GridRowId[]
}

const ragistrationDateSortComparator = (
  v1: string,
  v2: string,
  cellParams1: any,
  cellParams2: any,
) => {
  const row1 = cellParams1.api.getRow(cellParams1.id)
  const row2 = cellParams2.api.getRow(cellParams2.id)

  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return 1;
  if (v2 == null) return -1;

  if (row1?.registration_date && row2?.registration_date) {
    // 対象カラムでソートしてからregistration_dateでソートする
    return v1.localeCompare(v2) || row1.registration_date.localeCompare(row2.registration_date)
  }
  return 0
}

export const DeviceList: React.FC<DeviceListProps> = props => {
  // ダイアログ
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [selectedEmail, setSelectedEmail] = useState('')
  const [selectedSalesCompany, setSelectedSalesCompany] = useState('')
  const [openSalesCompanyEditDialog, setOpenSalesCompanyEditDialog] = useState(false)
  const auth = useAuth()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  const handleEditClick = () => {
    setOpenEditDialog(true)
  }
  const handleClose = () => {
    setOpenEditDialog(false)
  }
  const handleSalesCompanyEditClick = () => {
    setOpenSalesCompanyEditDialog(true)
  }
  const handleSalesCompanyEditClose = () => {
    setOpenSalesCompanyEditDialog(false)
  }

  const handleEdit = (id: string, newText: string) => {
    props.onEdit(id, newText)
    handleClose()
  }
  const handleSalesCompanyEdit = (id: string, newSalesCompany: string) => {
    props.onSalesCompanyEdit(id, newSalesCompany)
    handleSalesCompanyEditClose()
  }

  //時刻変換
  const conversionTime = (time: string): string => {
    const utcDate = new Date(time)
    const localDate = utcDate.toLocaleString()
    return localDate.slice(0, -3)
  }

  const columns: GridColDef[] = [
    {
      field: 'rhq',
      headerName: translation['label_RHQ_info'],
      editable: false,
      width: 70,
      renderCell: (params) => {
        return <div>{params.value}</div>;
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ["asc", "desc"],
    },
    {
      field: 'sales_company',
      headerName: translation['label_SalesCompany_info'],
      editable: false,
      width: 150,
      renderCell: (params) => {
        return (
          <div
            style={{
              cursor: params.field === 'sales_company' ? 'pointer' : 'default',
              display: 'block',
              whiteSpace: 'pre-wrap',
            }}
            onClick={(event) => {
              if (params.field === "sales_company") {
                handleSalesCompanyEditClick();
                setSelectedId(params.row.login_id);
                setSelectedSalesCompany(params.row.sales_company);
              }
            }}
          >
            {params.value}
          </div>
        )
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'login_id',
      headerName: translation['label_id'],
      editable: false,
      width: 150,
      renderCell: (params) => {
        const splitText = params.value.toString().match(/.{1,15}/g);

        return <div style={{ whiteSpace: 'pre-wrap' }}>{splitText?.join('\n')}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'password',
      headerName: translation['label_password'],
      editable: false,
      width: 150,
      renderCell: (params) => {
        const splitText = params.value.toString().match(/.{1,15}/g);

        return <div style={{ whiteSpace: 'pre-wrap' }}>{splitText?.join('\n')}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'api_key',
      headerName: translation['label_api_key'],
      editable: false,
      width: 300,
      renderCell: (params) => {
        const splitText = params.value.toString().match(/.{1,40}/g);

        return <div style={{ whiteSpace: 'pre-wrap' }}>{splitText?.join('\n')}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'device_id',
      headerName: translation['label_initial_setting_device_number'],
      editable: false,
      width: 220,
      renderCell: (params) => {
        return <div>{params.value}</div>;
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'email',
      headerName: translation['label_email'],
      editable: false,
      width: 200,
      renderCell: (params) => {
        const splitText = params.value? params.value.toString().match(/.{1,25}/g).join("\n") : "";
        return (
          <div
            style={{
              cursor: params.field === 'email' ? 'pointer' : 'default',
              display: 'block',
              whiteSpace: 'pre-wrap',
            }}
            onClick={(event) => {
              if (params.field === "email") {
                handleEditClick();
                setSelectedId(params.row.login_id);
                setSelectedEmail(params.row.email);
              }
            }}
          >
            {splitText}
          </div>
        )
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: "application_version",
      headerName: translation["label_app_version"],
      editable: false,
      width: 220,
      renderCell: (params) => {
        return <div>{params.value}</div>;
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ["asc", "desc"],
    },
    {
      field: 'registration_date',
      headerName: translation['label_registration_date'],
      editable: false,
      width: 150,
      renderCell: (params) => {
        return <div>{conversionTime(params.value)}</div>;
      },
      sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
      sortingOrder: ['asc', 'desc'],
    },
  ]

  const theme = createTheme(
    {
      components: {
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: 'none',
            },
            columnHeader: {
              borderBottom: '2px solid #000',
              '&:focus-within': {
                outline: 'none', // クリック時のヘッダー部の青枠を非表示
              },
            },
            columnHeaderTitle: {
              fontWeight: 'bold',
            },
            cell: {
              '&:focus-within': {
                outline: 'none', // クリック時のデータ部の青枠を非表示
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '&.Mui-checked': {
                color: '#282828',
              },
            },
          },
        },
      },
    },
    enUS,
  )

  return (
    <div style={{ height: 500, width: '100%' }}>
      <ThemeProvider theme={theme}>
        {openEditDialog && (
          <ModalDeviceEditDialog
            open={openEditDialog}
            title={translation['label_email']}
            id={selectedId}
            text={selectedEmail}
            onClose={handleClose}
            onSave={handleEdit}
          />
        )}

        {openSalesCompanyEditDialog && (
          <ModalDeviceSalesCompanyEditDialog
            open={openSalesCompanyEditDialog}
            title={translation['label_SalesCompany_info']}
            id={selectedId}
            text={selectedSalesCompany}
            onClose={handleSalesCompanyEditClose}
            onSave={handleSalesCompanyEdit}
          />
        )}

        <DataGrid
          checkboxSelection
          disableRowSelectionOnClick={auth.isDisable}
          rows={props.items}
          columns={columns}
          onRowSelectionModelChange={props.handleSelectionModelChange}
          rowSelectionModel={props.gridSelection}
          sx={{
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'visible',
            },
            '& .muidatagrid-root': {
              border: 'none',
            },
            height: 'calc(100vh - 310px)',
          }}
          // hideFooter
          disableColumnMenu
          localeText={{
            ...enUS.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: '',
            MuiTablePagination: {
              labelRowsPerPage: translation['label_rows_per_page'],
              labelDisplayedRows: ({ from, to, count }) =>
                (translation['label_display_separator'] ?? '')
                  .replace('{{item}}', `${from}\u2013${to}`)
                  .replace('{{item2}}', String(count)),
            },
          }}
        />
      </ThemeProvider>
    </div>
  )
}
